import { ApiRetailerPaymentType, ApiTransaction } from '@vend/types'
import { trackUserEvent } from '@vend/utilities'
import { PAYMENT_TYPES } from '@vend/business-components'
import { WEB_REGISTER } from '../../components/web-register.constant'
import { PAYMENT_STEPS } from '../../components/payments/integrated-payment/integrated-payment-steps.constant'
import { getFeatureFromCache } from '../../util/feature-cache'
import { isKlarnaPaymentType } from './klarna'
import { shouldUseDirectEconduit } from './econduit'

type PaymentCardEventData = {
  entry_method?: string
  card_brand?: string
  card_type?: string
  card_last_four?: string
  cardholder_name?: string
}

type PaymentCard = {
  entry_method?: string
  brand?: string
  type?: string
  last_four?: string
  cardholder_name?: string
}

type PaymentEMVEventData = {
  emv_aid?: string
  emv_application_label?: string
  emv_cryptogram_type?: string
  emv_cryptogram?: string
  emv_pin_verified?: boolean
  emv_tags?: any[]
}

export type ExternalPaymentEventData = {
  step: PAYMENT_STEPS
  approved_amount?: string
  message?: string
  payment_id?: string
  external_transaction_id?: string
  merchant_id?: string
  reference_id?: string
  signature_url?: string
  signature_base64?: string
  transaction_status?: string
  auth_code?: string
  receipt_html_extra?: string
} & PaymentEMVEventData &
  PaymentCardEventData &
  PaymentTerminalEventData

/**
 * Handle the payment card if there is a card property send through on the event data payload
 */
const handlePaymentCard = (eventData: PaymentCardEventData) => {
  if (
    eventData.entry_method ||
    eventData.card_brand ||
    eventData.card_type ||
    eventData.card_last_four ||
    eventData.cardholder_name
  ) {
    const card: PaymentCard = {}
    if (eventData.entry_method) {
      card.entry_method = eventData.entry_method.substring(0, 16)
    }
    if (eventData.card_brand) {
      card.brand = eventData.card_brand.substring(0, 20)
    }
    if (eventData.card_type) {
      card.type = eventData.card_type.substring(0, 6)
    }
    if (eventData.card_last_four) {
      card.last_four = eventData.card_last_four.substring(0, 4)
    }
    if (eventData.cardholder_name) {
      card.cardholder_name = eventData.cardholder_name
    }

    return card
  }
  return
}

/**
 * Terminal event data as received from payment integrations.
 */
export type PaymentTerminalEventData = {
  terminal_serial_number?: string
  terminal_model?: string
}

/**
 * Handle the payment terminal if there is a terminal property send through on the event data payload
 */
const extractPaymentTerminal = (eventData: PaymentTerminalEventData) => {
  if (eventData.terminal_serial_number || eventData.terminal_model) {
    const terminal: { serial_number?: string; model?: string } = {}
    if (eventData.terminal_serial_number) {
      terminal.serial_number = eventData.terminal_serial_number.substring(0, 36)
    }
    if (eventData.terminal_model) {
      terminal.model = eventData.terminal_model.substring(0, 36)
    }
    return terminal
  }
  return
}

/**
 * Handle the payment EMV if there is an emv property send through on the event data payload
 */
const extractPaymentEMV = (eventData: PaymentEMVEventData) => {
  if (
    eventData.emv_aid ||
    eventData.emv_application_label ||
    eventData.emv_cryptogram_type ||
    eventData.emv_cryptogram ||
    eventData.emv_pin_verified ||
    eventData.emv_tags
  ) {
    const emv: {
      aid?: string
      application_label?: string
      cryptogram_type?: string
      cryptogram?: string
      pin_verified?: boolean
      tags?: any[]
    } = {}
    if (eventData.emv_aid) {
      emv.aid = eventData.emv_aid.substring(0, 16)
    }
    if (eventData.emv_application_label) {
      emv.application_label = eventData.emv_application_label.substring(0, 16)
    }
    if (eventData.emv_cryptogram_type) {
      emv.cryptogram_type = eventData.emv_cryptogram_type.substring(0, 4)
    }
    if (eventData.emv_cryptogram) {
      emv.cryptogram = eventData.emv_cryptogram
    }
    if (eventData.emv_pin_verified) {
      emv.pin_verified = eventData.emv_pin_verified
    }
    if (eventData.emv_tags) {
      emv.tags = eventData.emv_tags
    }
    return emv
  }
  return
}

export type ExternalPaymentData = {
  external_transaction_id?: string
  source?: string
  source_id?: string
  merchant_id?: string
  reference_id?: string
  signature_url?: string
  signature_base64?: string
  transaction_status?: string
  auth_code?: string
  card?: ReturnType<typeof handlePaymentCard>
  terminal?: ReturnType<typeof extractPaymentTerminal>
  emv?: ReturnType<typeof extractPaymentEMV>
}

/**
 * Pure function to extract external payment data from event data.
 */
export function extractExternalPaymentData(
  eventData?: ExternalPaymentEventData
): ExternalPaymentData {
  const externalPaymentData: ExternalPaymentData = {}
  if (!eventData) {
    return externalPaymentData
  }

  if (eventData.external_transaction_id) {
    externalPaymentData.external_transaction_id = eventData.external_transaction_id.substring(
      0,
      36
    )
    externalPaymentData.source = 'Swiper'
    externalPaymentData.source_id = eventData.external_transaction_id.substring(
      0,
      255
    )
  }

  if (eventData.merchant_id) {
    externalPaymentData.merchant_id = eventData.merchant_id.substring(0, 36)
  }

  if (eventData.reference_id) {
    externalPaymentData.reference_id = eventData.reference_id.substring(0, 36)
  }

  if (eventData.signature_url) {
    externalPaymentData.signature_url = eventData.signature_url.substring(
      0,
      200
    )
  }

  if (eventData.signature_base64) {
    externalPaymentData.signature_base64 = eventData.signature_base64
  }

  if (eventData.transaction_status) {
    externalPaymentData.transaction_status = eventData.transaction_status
  }

  if (eventData.auth_code) {
    externalPaymentData.auth_code = eventData.auth_code.substring(0, 16)
  }

  externalPaymentData.card = handlePaymentCard(eventData)
  externalPaymentData.terminal = extractPaymentTerminal(eventData)
  externalPaymentData.emv = extractPaymentEMV(eventData)

  return externalPaymentData
}

export const trackIntegratedPaymentStep = ({
  paymentType,
  eventData,
}: {
  paymentType: ApiRetailerPaymentType
  eventData?: ExternalPaymentEventData
}) => {
  if (!eventData) {
    return
  }

  const { step, approved_amount = '', message = '' } = eventData

  trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Integrated Payment', {
    paymentId: eventData.payment_id,
    'Payment Type ID': paymentType.type_id,
    Outcome: step,
    approvedAmount: approved_amount,
    message,
  })
}

/**
 *  Get all external transaction ids for transaction list.
 */
export const getAllExternalTransactionIds = (
  transactions: ApiTransaction[]
) => {
  const externalTransactionIds: string[] = []
  transactions.forEach(transaction =>
    transaction.tenders.forEach(tender => {
      if (tender.type === 'SALE' && tender.external_transaction_id !== '') {
        externalTransactionIds.push(tender.external_transaction_id)
      }
    })
  )
  return externalTransactionIds
}

/**
 * If the payment type needs to check swiper db write health before processing the current payment
 */
export const shouldCheckSwiperMaintenance = (
  paymentType: ApiRetailerPaymentType
) => {
  switch (paymentType.type_id) {
    case PAYMENT_TYPES.WORLDPAY:
    case PAYMENT_TYPES.WORLDPAY_TRIPOS:
    case PAYMENT_TYPES.LSPAY:
    case PAYMENT_TYPES.AFTER_PAY:
      return true
    default: {
      // Klarna
      if (isKlarnaPaymentType(paymentType.type_id)) {
        return true
      }
      // econduit (direct)
      const paymentTypeConfig = paymentType.config
      const hasPaymentTypeUrl =
        paymentTypeConfig && paymentTypeConfig.url?.trim()
      if (
        hasPaymentTypeUrl &&
        shouldUseDirectEconduit(paymentType) &&
        getFeatureFromCache('direct_econduit')
      ) {
        return true
      }
      // other payments
      return false
    }
  }
}
